import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Badge, Btn, Input, Textarea, Select, Field, Modal } from "@/components/ui-kit";
import { BRANCHES, CLASSIFICATIONS, PROJECT_STATUSES, findMeta, fmtMoney, fmtDate, branchColor } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { FolderKanban, Plus } from "lucide-react";

export default function Projects() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => {
    const p = branchFilter ? { branch: branchFilter } : {};
    api.get("/projects", { params: p }).then((r) => setProjects(r.data));
  };
  useEffect(load, [branchFilter]);
  useEffect(() => { api.get("/users").then((r) => setSupervisors(r.data.filter((u) => ["supervisor", "admin"].includes(u.role)))); }, []);

  const canCreate = can("project.manage");

  return (
    <div className="space-y-4" data-testid="projects-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight sm:text-3xl">Projects</h1>
          <p className="text-sm text-muted-foreground">Army · Navy · Air Force · Commercial — classified project management</p>
        </div>
        <div className="flex gap-2">
          <Select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} className="w-40" data-testid="branch-filter">
            <option value="">All Branches</option>
            {BRANCHES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </Select>
          {canCreate && <Btn onClick={() => setOpen(true)} data-testid="new-project-btn"><Plus size={16} /> New Project</Btn>}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {projects.map((p) => {
          const clazz = findMeta(CLASSIFICATIONS, p.classification_level);
          return (
            <Card key={p.id} onClick={() => navigate(`/projects/${p.id}`)} data-testid={`project-card-${p.id}`}
              className="cursor-pointer overflow-hidden transition hover:border-primary/50 hover:shadow-lg">
              <div className="h-1.5" style={{ background: branchColor(p.client_branch) }} />
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-mono text-[11px] text-muted-foreground">{p.project_code}</div>
                    <h3 className="font-heading text-lg font-bold leading-tight">{p.title}</h3>
                  </div>
                  <FolderKanban size={18} className="shrink-0 text-muted-foreground" />
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Badge className="border-border" style={{ color: branchColor(p.client_branch) }}>{findMeta(BRANCHES, p.client_branch).label}</Badge>
                  <Badge className={clazz.cls}>{clazz.label}</Badge>
                  <Badge className="border-border text-muted-foreground">{p.status}</Badge>
                </div>
                <div className="mt-3 flex items-center justify-between text-sm">
                  <span className="font-mono text-xs text-muted-foreground">Budget</span>
                  <span className="font-mono font-bold">{fmtMoney(p.budget)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between font-mono text-[11px] text-muted-foreground">
                  <span>Start {p.start_date || "—"}</span><span>Target {p.target_end_date || "—"}</span>
                </div>
              </div>
            </Card>
          );
        })}
        {projects.length === 0 && <p className="text-sm text-muted-foreground">No projects yet.</p>}
      </div>

      {open && <ProjectForm supervisors={supervisors} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function ProjectForm({ supervisors, onClose, onSaved }) {
  const [f, setF] = useState({
    title: "", client_branch: "army", classification_level: "unclassified", status: "planning",
    budget: 0, supervisor_id: "", start_date: "", target_end_date: "", description: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.title) { toast.error("Title required"); return; }
    try { await api.post("/projects", { ...f, budget: Number(f.budget), supervisor_id: f.supervisor_id || null }); toast.success("Project created"); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title="New Project" wide>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Project Title" required><Input data-testid="pf-title" value={f.title} onChange={set("title")} /></Field>
        <Field label="Client Branch"><Select value={f.client_branch} onChange={set("client_branch")} data-testid="pf-branch">{BRANCHES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}</Select></Field>
        <Field label="Classification"><Select value={f.classification_level} onChange={set("classification_level")} data-testid="pf-class">{CLASSIFICATIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</Select></Field>
        <Field label="Status"><Select value={f.status} onChange={set("status")}>{PROJECT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</Select></Field>
        <Field label="Budget (₹)"><Input type="number" value={f.budget} onChange={set("budget")} /></Field>
        <Field label="Supervisor"><Select value={f.supervisor_id} onChange={set("supervisor_id")}><option value="">—</option>{supervisors.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}</Select></Field>
        <Field label="Start Date"><Input type="date" value={f.start_date} onChange={set("start_date")} /></Field>
        <Field label="Target End"><Input type="date" value={f.target_end_date} onChange={set("target_end_date")} /></Field>
      </div>
      <Field label="Description"><Textarea className="mt-3" rows={2} value={f.description} onChange={set("description")} /></Field>
      <div className="mt-4 flex justify-end gap-2"><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="project-submit-btn">Create</Btn></div>
    </Modal>
  );
}
